export const DESTINATION_TYPE = {
  OBJECT_STORAGE: "object_storage",
  FHIR_SERVER: "fhir_server",
} as const;

export type DestinationType = (typeof DESTINATION_TYPE)[keyof typeof DESTINATION_TYPE];

export const CLOUD_VENDOR = {
  AWS: "aws",
  GCS: "gcs",
  AZURE: "azure",
} as const;

export type CloudVendor = (typeof CLOUD_VENDOR)[keyof typeof CLOUD_VENDOR];

export type StorageTarget = "minio" | "aws" | "gcs" | "azure";

export type ExportFileType = "ndjson" | "zipped-ndjson";

export type CompressionMode = "none" | "gzip";

export type FhirAuthType = "none" | "oauth2_client_credentials" | "bearer_token";

export type ExportRunStatus = "queued" | "running" | "completed" | "partial" | "failed";

export type ExportTrigger =
  | "pull_completed"
  | "destination_added"
  | "manual_retry"
  | "reactivated"
  | "sample_load_complete"
  | "fhir_import_complete";

export type ExportItemType = "ndjson_file" | "fhir_bundle";

export type CompletedPull = {
  pullId: string;
  accountId: string;
  organizationId: string;
};


export type ExportRun = {
  id: string;
  destinationId: string;
  organizationId: string;
  status: ExportRunStatus;
  trigger: ExportTrigger;
  triggerAccountId: string | null;
  deltaFrom: Date | null;
  startedAt: Date;
  completedAt: Date | null;
  errorMessage: string | null;
  accountsTotal: number | null;
  patientsTotal: number | null;
  patientsSucceeded: number;
  patientsFailed: number;
  itemsExported: number;
};

export type ExportItem = {
  id: number;
  runId: string;
  patientId: string;
  accountId: string;
  status: "pending" | "completed" | "failed";
  itemType: ExportItemType;
  objectKey: string | null;
  resourceCount: number | null;
  errorMessage: string | null;
  retryCount: number;
  completedAt: Date | null;
};

export type ObjectStorageExportOptions = {
  endpoint: string;
  region: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
  cloudVendor: CloudVendor;
  storageTarget: StorageTarget;
  fileType: ExportFileType;
  compression: CompressionMode;
  forcePathStyle: boolean;
  organizationId: string;
};

export type FhirServerExportOptions = {
  fhirServerUrl: string;
  authType: FhirAuthType;
  tokenEndpoint: string | null;
  clientId: string | null;
  clientSecret: string | null;
  scope: string | null;
  bearerToken: string | null;
  organizationId: string;
};

export type DestinationRow = {
  id: string;
  organization_id: string;
  name: string;
  enabled: boolean;
  destination_type: string;
  cloud_vendor: string | null;
  storage_target: string | null;
  file_type: string | null;
  endpoint: string | null;
  region: string | null;
  bucket: string | null;
  access_key_id: string | null;
  secret_access_key: string | null;
  fhir_server_url: string | null;
  auth_type: FhirAuthType | null;
  token_endpoint: string | null;
  client_id: string | null;
  client_secret: string | null;
  scope: string | null;
  bearer_token: string | null;
  is_internal: boolean;
  last_exported_at: Date | null;
  created_at: Date;
  updated_at: Date;
};

export type DestinationListRow = DestinationRow & {
  lastRunStatus: string | null;
  lastRunCompletedAt: Date | null;
  lastRunPatients: number | null;
  lastRunResources: number | null;
  activeRunStatus: string | null;
  activeRunAccountsTotal: number | null;
  activeRunAccountsProcessed: number | null;
  activeRunStartedAt: Date | null;
  lastFailedAt: Date | null;
  lastFailedError: string | null;
};

export type FhirExportContext = {
  fhirServerUrl: string;
  authType: FhirAuthType;
  tokenEndpoint: string | null;
  clientId: string | null;
  clientSecret: string | null;
  scope: string | null;
  bearerToken: string | null;
};

export type ExportOrgResult = {
  totalDestinations: number;
  succeeded: number;
  failed: number;
};

export type ResourceIdentity = {
  resourceType: string;
  resourceId: string;
};

export type ResourceEdge = {
  source: string;
  target: string;
};

/** A run's snapshot row that a newer version replaced after the run started, whose edges the table no longer holds. */
export type ReplacedSnapshotRow = {
  readonly id: string;
  readonly account_id: string;
  readonly resource_type: string;
  readonly resource_id: string;
};

export type DependencyState = {
  depsCount: Map<string, number>;
  dependentsByTarget: Map<string, string[]>;
  outgoingBySource: Map<string, string[]>;
  pending: Set<string>;
  ready: string[];
};

export type PrunedReferenceAudit = {
  field: "reference" | "url";
  originalValue: string;
  path: string;
  reason: "missing_target_in_source_snapshot" | "unparseable_reference_shape";
  target: string;
};

export type ExportEventListener = (payload: string) => void;

export type Writer = {
  writeLine: (line: string) => Promise<void>;
  finalize: () => Promise<void>;
  stream: import("node:stream").Readable;
  /** Number of lines written to this writer. Incremented by writeLine. */
  resourceCount: number;
};

export type StorageClient =
  | { kind: "s3"; client: import("@aws-sdk/client-s3").S3Client }
  | { kind: "gcs"; client: import("@google-cloud/storage").Storage }
  | { kind: "azure"; client: import("@azure/storage-blob").BlobServiceClient };

export type InflightUpload = {
  writer: Writer;
  promise: Promise<void>;
};

export type ExportOptions = {
  jobId?: string;
  organizationId?: string;
  compression: CompressionMode;
  cloudVendor: CloudVendor;
  storageTarget: StorageTarget;
  endpoint: string;
  region: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
  forcePathStyle: boolean;
  fetchBatchSize: number;
  destinationId?: string;
  triggerAccountId?: string | null;
  /**
   * Run watermark (typically `export_run.started_at`, PG-authoritative).
   * When set, bounds the resource SELECT by `valid_from <= runStartedAt`
   * AND is persisted into `export_checkpoint.last_exported_at` once the
   * upload succeeds. The run's lower bound (delta) comes from the prior
   * checkpoint.
   */
  runStartedAt?: Date;
};

export type ExportStats = {
  rowsRead: number;
  rowsSkipped: number;
  rowsExported: number;
  patientsExported: number;
  filesUploaded: number;
};

export type PromotedRun = {
  runId: string;
  triggerAccountId: string | null;
  trigger: ExportTrigger;
  triggeredBy: string | null;
};

export type ExportItemEvent = {
  runId: string;
  destinationId: string;
  organizationId: string;
  patientId: string;
  accountId: string;
  success: boolean;
  resourceCount: number | null;
};

export type ExportRunEvent = {
  runId: string;
  destinationId: string;
  organizationId: string;
  status: ExportRunStatus;
};

export type ExportProgressEvent = {
  runId: string;
  destinationId: string;
  organizationId: string;
  resourcesPosted: number;
  resourcesTotal: number;
};

export type OrgBundleExportResult = {
  resourcesTotal: number;
  resourcesPosted: number;
  successfulKeys: Set<string>;
  /** Keys that were in a chunk that HAPI rejected (direct failures). */
  failedKeys: Set<string>;
  /** `exportKeys \ successfulKeys` — direct failures plus keys left in limbo. */
  unpostedKeys: Set<string>;
  /** Per-key error message for keys in `failedKeys` (direct failures only). */
  failedKeyErrors: Map<string, string>;
  runError: string | null;
};

// --------------------------------------------------------------------------
// Internal helpers for fhirExport.ts / exporter.ts. Module-local in intent but
// lifted here so no types live alongside their module (per project convention).
// --------------------------------------------------------------------------

export type TokenCacheEntry = {
  accessToken: string;
  expiresAt: number;
};

export type ChunkResult =
  | { ok: true; keys: string[] }
  | { ok: false; keys: string[]; error: string };

export type PostChunksArgs = {
  runStartedAt: Date;
  chunks: string[][];
  concurrency: number;
  accountIds: string[];
  idToSafeMap: Map<string, string>;
  presentRefs: Set<string>;
  sourceFhirBaseUrls: Set<string>;
  patientIdsByResourceKey: Map<string, string[]>;
  baseUrl: string;
  headers: Record<string, string>;
  runId: string;
  destinationId: string;
  organizationId: string;
  resourcesTotal: number;
  layerIndex: number;
  cycleFallback: boolean;
};

export type BundleRow = {
  resource_type: string;
  resource_id: string;
  hash: string;
  valid_from: Date;
  resource: unknown;
};

// Shared param bag for run-end helpers that query each account's delta
// filtered down to a specific set of unposted keys (both
// `resolveFailedAccountsByUnposted` and `fetchAccountDeltaUnpostedKeys` take
// this shape — two views over the same per-account × unposted-key intersection).
export type AccountDeltaQueryArgs = {
  destId: string;
  accountIds: string[];
  runStartedAt: Date;
  unpostedKeys: Set<string>;
};

export type CheckpointAdvanceArgs = {
  destId: string;
  accountIds: string[];
  runStartedAt: Date;
  failedAccountSet: Set<string>;
};

export type RunEndExportItemsArgs = {
  runId: string;
  destId: string;
  accountIds: string[];
  accountToUser: Map<string, string>;
  runStartedAt: Date;
  failedAccountSet: Set<string>;
  failedKeys: Set<string>;
  unpostedKeys: Set<string>;
  failedKeyErrors: Map<string, string>;
  runError: string | null;
};

// The owning patient ids addPatientIdentity writes into an exported Patient
// resource copy's identifier array.
export type PatientIdentity = {
  externalPatientIds: string[];
};

export type PullCompletionTimer = ReturnType<typeof setInterval>;
